import { Injectable } from '@angular/core';
import Dexie from 'dexie';
import * as CryptoJS from 'crypto-js';
import { environment } from '../../../../../../environments/environment';
import { PollyManageDataService } from '../../../@core/services';

export interface ApiResponse<T> {
  url: string;
  id: string;
  data: T;
  timestamp: number;
  environment: string;
}

interface EncryptedApiResponse {
  url: string;
  id: string;
  data: string;
  timestamp: number;
  environment: string; 
}

@Injectable({
  providedIn: 'root'
})
export class ApiCachingService extends Dexie {
  cachedData!: Dexie.Table<EncryptedApiResponse, [string, string, string]>;
  private readonly userIdPromise: Promise<void>;
  private secretKey!: string;
  private id!: string;

  constructor(private readonly manageDataService:PollyManageDataService) {
    super('OmixAtlas');
    this.version(1).stores({
        cachedData: '[url+environment+id]',  // Composite key
        cachedDate: 'id'
    });
    this.userIdPromise = this.initUserId();
  }

  private async initUserId(): Promise<void> {
    return new Promise((resolve) => {
        this.manageDataService.userResponse$.subscribe((userInfo) => {
            if (userInfo && userInfo.data.id) {
                this.id = userInfo.data.id;
                this.secretKey = environment.cognitoClientId + this.id;
                this.flushCachedResponseChecks();
            }
            resolve();
        });
    });
  }

  async getResponse<T>(url: string): Promise<ApiResponse<T> | undefined> {
    await this.userIdPromise;
    const encryptedResponse: EncryptedApiResponse | undefined = await this.cachedData.get([url, environment.environment, this.id]);
    if (!encryptedResponse) {
        return undefined;
    }
    return {
        url: encryptedResponse.url,
        id: encryptedResponse.id,
        data: this.decrypt<T>(encryptedResponse.data),
        timestamp: encryptedResponse.timestamp,
        environment: encryptedResponse.environment
    };
  }

  async setResponse<T>(response: ApiResponse<T>): Promise<void> {
    const env = environment.environment;
    await this.cachedData.put({
        url: response.url,
        id: response.id,
        data: this.encrypt(response.data),
        timestamp: response.timestamp,
        environment: env
    });
  }
  /**
  * Get cached data and return immediately if available and trigger an background cache refresh
  **/
  async cacheApiCall<T>(url: string, call: () => Promise<T>): Promise<T> {
    let cachedData: T | null = null;
    if (this.id !=='') {
      try {
        const cachedResponse = await this.getResponse<T>(url);
        if (cachedResponse) {
            cachedData = cachedResponse.data;
        }
      } catch (error) {
        console.error("Error fetching data from cache", error);
      }
    }
    if (cachedData) {
      this.refreshCacheInBackground(url, call);
      return cachedData;
    } else {
      try {
          const freshData = await call();
          if(!!freshData) {
            this.setResponse({
                url,
                id: this.id,
                data: freshData,
                timestamp: Date.now(),
                environment: environment.environment
            });
            this.table('cachedDate').put({ id: this.id, date: new Date().toISOString().split('T')[0] });
          }
          return freshData;
      } catch (apiError) {
          console.error("Error fetching data from the API", apiError);
          throw apiError;
      }
    }
  }

  private encrypt<T>(data: T): string {
    return CryptoJS.AES.encrypt(JSON.stringify(data), this.secretKey).toString();
  }

  private decrypt<T>(encryptedData: string): T {
    const bytes = CryptoJS.AES.decrypt(encryptedData, this.secretKey);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  }

  private async refreshCacheInBackground<T>(url: string, call: () => Promise<T>): Promise<void> {
    try {
        const freshData = await call();
        if (!!freshData) {
            await this.setResponse({
                url,
                id: this.id,
                data: freshData,
                timestamp: Date.now(),
                environment: environment.environment
            });
        await this.table('cachedDate').put({ id: this.id, date: new Date().toISOString().split('T')[0] });
        }
    } catch (error) {
        console.error("Error in background cache refresh", error);
    }
  }

  private async flushCachedResponseChecks(): Promise<void> {
    await this.userIdPromise;
    if(this.id!=='') {
      await Promise.all([this.removeNonMatchingEnvsEntries(), this.clearOldCache()]);
    }
  }

  private async removeNonMatchingEnvsEntries(): Promise<void> {
    const currentEnv = environment.environment;
    const dataEntries = await this.cachedData.toArray();
    const entriesToDelete = dataEntries.filter(entry => 
        entry.environment !== currentEnv || entry.id !== this.id
    );
    for (const entry of entriesToDelete) {
        await this.cachedData.delete([entry.url, entry.environment, entry.id]);
    }
  }

  private async clearOldCache(): Promise<void> {
    const currentCacheDate = await this.table('cachedDate').get(this.id);
    const dateToday = new Date();
    dateToday.setHours(0,0,0,0);
    if (!currentCacheDate || new Date(currentCacheDate.date) < dateToday) {
        await this.table('cachedData').clear();
    }
  }
}
